// src/components/sourcex/TemplateAuditModal.jsx
//
// The audit trail for a source-document template — who changed what, when, and
// from what to what.
//
// Same shape as the patient audit timeline (PatientAuditTimeline.jsx): newest
// first, one row per event, an action chip, the actor, and a field-level
// from -> to list where there is one. Read from the append-only
// sourcex_template_audits collection (templateAuditService.js).
//
// Filtered to the selected visit by default, because that is the unit someone
// works in — with a toggle for the whole template, which is where events that
// aren't about one visit live (approvals, rebuilds, adding or deleting a
// visit). Without that toggle those events would be invisible.
//
// Presentation only. It cannot write, and audit events are never edited.

import { useMemo, useState } from "react";
import { ScrollText, X, Loader2, Filter } from "lucide-react";
import { TEMPLATE_AUDIT_ACTIONS } from "../../services/templateAuditService";

const A = TEMPLATE_AUDIT_ACTIONS;

const ACTION_META = {
  [A.QUESTION_ADDED]: { label: "Added", tone: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  [A.QUESTION_EDITED]: { label: "Edited", tone: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  [A.QUESTION_DELETED]: { label: "Deleted", tone: "text-rose-200 bg-rose-400/15 border-rose-400/30" },
  [A.QUESTION_MOVED]: { label: "Moved", tone: "text-sky-200 bg-sky-400/15 border-sky-400/30" },
  [A.CONDITION_CHANGED]: { label: "Condition", tone: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  [A.HEADING_ADDED]: { label: "Heading added", tone: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  [A.HEADING_RENAMED]: { label: "Heading renamed", tone: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  [A.HEADING_DELETED]: { label: "Heading deleted", tone: "text-rose-200 bg-rose-400/15 border-rose-400/30" },
  [A.SECTION_MERGED]: { label: "Sections merged", tone: "text-sky-200 bg-sky-400/15 border-sky-400/30" },
  [A.VISIT_ADDED]: { label: "Visit added", tone: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  [A.LOG_ADDED]: { label: "Log added", tone: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  [A.ELIGIBILITY_ADDED]: { label: "I/E criteria added", tone: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30" },
  [A.VISIT_RENAMED]: { label: "Visit renamed", tone: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  [A.VISIT_DELETED]: { label: "Visit deleted", tone: "text-rose-200 bg-rose-400/15 border-rose-400/30" },
  [A.VISIT_MOVED]: { label: "Visit moved", tone: "text-sky-200 bg-sky-400/15 border-sky-400/30" },
  [A.TEMPLATE_APPROVED]: { label: "Approved", tone: "text-emerald-100 bg-emerald-500/25 border-emerald-400/50" },
  [A.PAPER_FINAL_MARKED]: { label: "Paper final", tone: "text-amber-100 bg-amber-400/20 border-amber-400/40" },
  [A.PAPER_FINAL_CLEARED]: { label: "Paper final removed", tone: "text-slate-200 bg-white/10 border-white/20" },
  [A.VERSION_RESTORED]: { label: "Restored", tone: "text-purple-200 bg-purple-500/20 border-purple-300/40" },
  [A.TEMPLATE_BUILT]: { label: "Rebuilt", tone: "text-rose-200 bg-rose-400/15 border-rose-400/30" },
  [A.STANDARD_BLOCK_NOTICE]: { label: "Standard notice", tone: "text-amber-200 bg-amber-400/15 border-amber-400/30" },
  [A.QUESTION_COPIED]: { label: "Question copied", tone: "text-sky-200 bg-sky-400/15 border-sky-400/30" },
};

const FALLBACK = { label: "Change", tone: "text-slate-300 bg-white/10 border-white/20" };

// serverTimestamp() is null for the instant between the local write and the
// server's ack, so a just-logged event has no time yet. Say so rather than
// rendering "Invalid Date".
const formatWhen = (ts) => {
  if (!ts) return "just now";
  const d = typeof ts?.toDate === "function" ? ts.toDate() : new Date(ts);
  return Number.isNaN(d?.getTime?.())
    ? "just now"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const actorLine = (e) => {
  const name = String(e.actorName || "").trim();
  const initials = String(e.actorInitials || "").trim();
  const who = name ? (initials ? `${name} (${initials})` : name) : "";
  return who ? `${who} · ${e.actorEmail || ""}` : e.actorEmail || "unknown";
};

/**
 * @param {Array} events   newest first, from listTemplateAuditEvents
 * @param {string} eventId the visit selected in the editor
 * @param {string} eventName
 * @param {boolean} loading
 * @param {() => void} onClose
 */
export default function TemplateAuditModal({
  events,
  eventId = "",
  eventName = "",
  loading = false,
  onClose,
}) {
  const all = useMemo(() => (Array.isArray(events) ? events : []), [events]);
  const [scope, setScope] = useState(eventId ? "visit" : "all");

  const shown = useMemo(() => {
    if (scope === "all") return all;
    // This visit's own events, plus the template-wide ones (approvals,
    // rebuilds) that apply to it as much as to any other visit.
    return all.filter((e) => !e.eventId || e.eventId === eventId);
  }, [all, scope, eventId]);

  const scopeBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setScope(key)}
      className={`px-2 py-1 rounded-lg text-[11px] font-medium border transition ${
        scope === key
          ? "border-purple-300/50 bg-purple-500/20 text-white"
          : "border-white/10 bg-white/[0.04] text-slate-400 hover:text-white hover:bg-white/[0.09]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl border border-white/10 bg-[#0c0726] shadow-2xl">
        <div className="shrink-0 px-5 pt-5 pb-3 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
                <ScrollText size={18} /> Audit trail
              </h2>
              <p className="mt-0.5 text-xs text-slate-400">
                Every change to this template, newest first. Append-only — entries
                are never edited or removed.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
              <X size={18} />
            </button>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <Filter className="w-3 h-3 text-slate-500" />
            {eventId && scopeBtn("visit", eventName || "This visit")}
            {scopeBtn("all", "Whole template")}
            <span className="ml-auto text-[11px] tabular-nums text-slate-500">
              {shown.length} entr{shown.length === 1 ? "y" : "ies"}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4">
          {loading ? (
            <div className="py-8 flex items-center justify-center gap-2 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Nothing recorded yet. Every edit from here on is logged.
            </p>
          ) : (
            <ul className="space-y-2">
              {shown.map((e) => {
                const meta = ACTION_META[e.action] || FALLBACK;
                return (
                  <li
                    key={e.id}
                    className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${meta.tone}`}
                          >
                            {meta.label}
                          </span>
                          <span className="text-sm text-slate-100">{e.title}</span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {actorLine(e)}
                          {/* Which visit, when looking at the whole template —
                              otherwise two identically-worded edits in
                              different visits are indistinguishable. */}
                          {scope === "all" && e.eventName ? ` · ${e.eventName}` : ""}
                        </p>
                      </div>
                      <span className="shrink-0 text-[11px] text-slate-500 whitespace-nowrap">
                        {formatWhen(e.createdAt)}
                      </span>
                    </div>

                    {Array.isArray(e.changes) && e.changes.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 pl-1">
                        {e.changes.map((c) => (
                          <li key={c.field} className="text-[11px] text-slate-300">
                            <span className="text-slate-500">{c.label}: </span>
                            <span className="line-through text-slate-500">{c.from}</span>
                            <span className="mx-1 text-slate-500">→</span>
                            <span className="text-slate-100">{c.to}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
